import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';
import { debounce } from 'lodash';

interface AutoSaveState {
  isEnabled: boolean;
  lastSaved: Date | null;
  pendingChanges: boolean;
  saving: boolean;
  error: string | null;
}

export const useRealtimeReviewSession = (queueItemId: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>({
    isEnabled: true,
    lastSaved: null,
    pendingChanges: false,
    saving: false,
    error: null
  });
  
  const sessionDataRef = useRef<any>({});
  const channelRef = useRef<any>(null);

  // Debounced auto-save function using localStorage
  const debouncedAutoSave = useCallback(
    debounce(async (data: any) => {
      if (!autoSaveState.isEnabled || autoSaveState.saving) return;

      setAutoSaveState(prev => ({ ...prev, saving: true, error: null }));

      try {
        // Save to localStorage as backup
        const sessionKey = `review_session_${queueItemId}_${user?.id}`;
        localStorage.setItem(sessionKey, JSON.stringify({
          data,
          timestamp: new Date().toISOString(),
          queueItemId,
          userId: user?.id
        }));

        setAutoSaveState(prev => ({
          ...prev,
          saving: false,
          lastSaved: new Date(),
          pendingChanges: false
        }));

      } catch (error: any) {
        console.error('Auto-save failed:', error);
        setAutoSaveState(prev => ({
          ...prev,
          saving: false,
          error: error.message
        }));
      }
    }, 2000),
    [autoSaveState.isEnabled, autoSaveState.saving, queueItemId, user?.id]
  );

  // Initialize or restore review session
  const initializeSession = useCallback(() => {
    if (!user?.id || !queueItemId) return;

    try {
      // Try to restore from localStorage
      const sessionKey = `review_session_${queueItemId}_${user.id}`;
      const saved = localStorage.getItem(sessionKey);
      
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        sessionDataRef.current = data || {};
        setAutoSaveState(prev => ({
          ...prev,
          lastSaved: new Date(timestamp)
        }));
      }

    } catch (error: any) {
      console.error('Failed to restore review session:', error);
    }
  }, [user?.id, queueItemId]);

  // Update session data
  const updateSessionData = useCallback((updates: any) => {
    sessionDataRef.current = { ...sessionDataRef.current, ...updates };
    
    setAutoSaveState(prev => ({ ...prev, pendingChanges: true }));
    
    if (autoSaveState.isEnabled) {
      debouncedAutoSave(sessionDataRef.current);
    }
  }, [autoSaveState.isEnabled, debouncedAutoSave]);

  // Manual save
  const saveSession = useCallback(async () => {
    setAutoSaveState(prev => ({ ...prev, saving: true, error: null }));

    try {
      const sessionKey = `review_session_${queueItemId}_${user?.id}`;
      localStorage.setItem(sessionKey, JSON.stringify({
        data: sessionDataRef.current,
        timestamp: new Date().toISOString(),
        queueItemId,
        userId: user?.id
      }));

      setAutoSaveState(prev => ({
        ...prev,
        saving: false,
        lastSaved: new Date(),
        pendingChanges: false
      }));

      return true;
    } catch (error: any) {
      console.error('Manual save failed:', error);
      setAutoSaveState(prev => ({
        ...prev,
        saving: false,
        error: error.message
      }));
      return false;
    }
  }, [queueItemId, user?.id]);

  // Toggle auto-save
  const toggleAutoSave = useCallback((enabled: boolean) => {
    setAutoSaveState(prev => ({ ...prev, isEnabled: enabled }));
  }, []);

  // Set up real-time subscription for queue item changes
  useEffect(() => {
    if (!queueItemId || !user?.id) return;

    const channel = supabase
      .channel(`review-session-${queueItemId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bill_upload_queue',
          filter: `id=eq.${queueItemId}`
        },
        (payload) => {
          console.log('Queue item updated remotely:', payload);
          
          // Invalidate queries to refresh data
          queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
          
          if (payload.new?.processing_status === 'completed') {
            toast({
              title: "Processing Complete",
              description: "The document processing has been completed",
            });
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [queueItemId, user?.id, toast, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending auto-save
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  return {
    sessionData: sessionDataRef.current,
    autoSaveState,
    updateSessionData,
    saveSession,
    toggleAutoSave,
    initializeSession
  };
};